import {
  HighwayPoiSpec,
  IdGenerator,
  LoadHandlingPoiSpec,
  RackSpec,
  StationaryPoiSpec,
} from '@thive/fddm-types';
import { useFloorplanDesignStore } from '../store/useFloorplanDesignStore';

export const ID_GENERATOR_DEFAULT: IdGenerator = {
  format: 'default%04d',
  startFirstId: 1,
  incrementFirstId: 1,
  additionalIncrementers: [],
};

export const LOAD_HANDLING_POI_SPEC_DEFAULT = (): LoadHandlingPoiSpec => {
  const { getPreference } = useFloorplanDesignStore.getState();
  const canBeUsedForParking = getPreference(
    'singularStationArea.loadHandlingPoiSpecs.canBeUsedForParking',
  );

  return {
    vehicleTypeId: '',
    idGenerator: ID_GENERATOR_DEFAULT,
    angle: 0.0,
    deltaX: getPreference('singularStationArea.loadHandlingPoiSpecs.deltaX'),
    deltaY: getPreference('singularStationArea.loadHandlingPoiSpecs.deltaY'),
    alwaysFitStation: getPreference('singularStationArea.loadHandlingPoiSpecs.alwaysFitStation'),
    crabSetting: getPreference('singularStationArea.loadHandlingPoiSpecs.crabSetting'),
    curvature: getPreference('singularStationArea.loadHandlingPoiSpecs.curvature'),
    canBeUsedForParking,
    parkingExtension: canBeUsedForParking
      ? {
          priority: getPreference(
            'singularStationArea.loadHandlingPoiSpecs.parkingExtension.priority',
          ),
        }
      : undefined,
  };
};

export const getHighwayPoiSpecDefault = (): HighwayPoiSpec => {
  const { getPreference } = useFloorplanDesignStore.getState();
  return {
    vehicleTypeId: '',
    idGenerator: ID_GENERATOR_DEFAULT,
    horizontalSpacing: {
      minMargin: getPreference('highwayArea.highwayPoiSpecs.horizontalSpacing.minMargin'),
      minGap: getPreference('highwayArea.highwayPoiSpecs.horizontalSpacing.minGap'),
    },
    verticalSpacing: {
      minMargin: getPreference('highwayArea.highwayPoiSpecs.verticalSpacing.minMargin'),
      minGap: getPreference('highwayArea.highwayPoiSpecs.verticalSpacing.minGap'),
    },
    roadType: getPreference('highwayArea.highwayPoiSpecs.roadType'),
    crabSetting: getPreference('highwayArea.highwayPoiSpecs.crabSetting'),
    curvature: getPreference('highwayArea.highwayPoiSpecs.curvature'),
    horizontalDistribution: getPreference('highwayArea.highwayPoiSpecs.horizontalDistribution'),
    verticalDistribution: getPreference('highwayArea.highwayPoiSpecs.verticalDistribution'),
  };
};

export const POI_SPEC_DEFAULT = (): StationaryPoiSpec => {
  const { getPreference } = useFloorplanDesignStore.getState();
  return {
    vehicleTypeId: '',
    idGenerator: ID_GENERATOR_DEFAULT,
    crabSetting: getPreference('stationaryArea.stationaryPoiSpecs.crabSetting'),
    curvature: getPreference('stationaryArea.stationaryPoiSpecs.curvature'),
  };
};

export const getRackSpecDefault = (): RackSpec => {
  const { getPreference } = useFloorplanDesignStore.getState();
  return {
    bayLength: getPreference('rackingArea.rackSpec.bayLength'),
    bayDepth: getPreference('rackingArea.rackSpec.bayDepth'),
    bayHeight: getPreference('rackingArea.rackSpec.bayHeight'),
    beamLength: getPreference('rackingArea.rackSpec.beamLength'),
    beamHeight: getPreference('rackingArea.rackSpec.beamHeight'),
    clearanceSide: getPreference('rackingArea.rackSpec.clearanceSide'),
    clearanceInBetween: getPreference('rackingArea.rackSpec.clearanceInBetween'),
    clearanceTop: getPreference('rackingArea.rackSpec.clearanceTop'),
    clearanceDepth: getPreference('rackingArea.rackSpec.clearanceDepth'),
    overhangFront: getPreference('rackingArea.rackSpec.overhangFront'),
    overhangBack: getPreference('rackingArea.rackSpec.overhangBack'),
    topShelfHeight: getPreference('rackingArea.rackSpec.topShelfHeight'),
    numColumnsInBay: getPreference('rackingArea.rackSpec.numColumnsInBay'),
    numStationsInDepth: getPreference('rackingArea.rackSpec.numStationsInDepth'),
    shelfHeights: [1, 2, 3, 4, 5],
    loadTypes: [
      {
        loadTypeId: '',
        stationRegex: undefined,
      },
    ],
  };
};

export const MIN_HIGHWAY_THICKNESS = 1;
